import { Vector2 } from "three";
import { normalizePosition, TraceIntersection, uvToDocumentXY } from "../common";
export class EventManager {
    constructor(canvas, preview, scene, camera) {
        Object.defineProperty(this, "canvas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "preview", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "scene", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "camera", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "product", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "eventMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastIntersection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "CLICK_DISTANCE_THRESHOLD", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 10
        });
        Object.defineProperty(this, "mouseDownPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "onMouseDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                const intersection = this.getIntersection(event);
                if (intersection) {
                    this.mouseDownPosition = new Vector2(event.clientX, event.clientY);
                }
            }
        });
        Object.defineProperty(this, "onMouseUp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                const intersection = this.getIntersection(event);
                // If the mouse up is over a surface and the mouse down was also over a surface
                // check if the click distance is within a limited threshold to avoid triggering on model dragging
                if (intersection && this.mouseDownPosition) {
                    const mouseUpPosition = new Vector2(event.clientX, event.clientY);
                    const clickDistance = this.mouseDownPosition.distanceTo(mouseUpPosition);
                    if (clickDistance < this.CLICK_DISTANCE_THRESHOLD) {
                        this.triggerEvents("click", intersection.surface, intersection.intersection);
                    }
                }
                // Reset the mouse down or the next event
                this.mouseDownPosition = undefined;
            }
        });
        Object.defineProperty(this, "onMouseMove", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                var _a, _b, _c;
                const intersection = this.getIntersection(event);
                // If there is a lastInteraction and this is not the same as current object: mouseout
                if (this.lastIntersection && ((_a = this.lastIntersection) === null || _a === void 0 ? void 0 : _a.intersection.object.id) !== ((_b = intersection === null || intersection === void 0 ? void 0 : intersection.intersection) === null || _b === void 0 ? void 0 : _b.object.id)) {
                    this.triggerEvents("mouseout", this.lastIntersection.surface, this.lastIntersection.intersection);
                }
                // If there is an interaction and it's not the same as the lastInteraction: mousein
                if (intersection && ((_c = this.lastIntersection) === null || _c === void 0 ? void 0 : _c.intersection.object.id) !== intersection.intersection.object.id) {
                    this.triggerEvents("mousein", intersection.surface, intersection.intersection);
                }
                if (intersection) {
                    this.triggerEvents("mousemove", intersection.surface, intersection.intersection);
                }
                this.lastIntersection = intersection;
            }
        });
        this.canvas = canvas;
        this.preview = preview;
        this.scene = scene;
        this.camera = camera;
        this.eventMap = {
            click: [],
            mousein: [],
            mouseout: [],
            mousemove: [],
            stateChange: [],
            cameraChange: [],
        };
        canvas.addEventListener("mousedown", this.onMouseDown, false);
        canvas.addEventListener("mouseup", this.onMouseUp, false);
        canvas.addEventListener("mousemove", this.onMouseMove, false);
    }
    addListener(type, listener) {
        // Not sure why the if statement is needed because typescript should understand the typing used in map but it doesn't
        if (type === "stateChange") {
            this.eventMap.stateChange.push(listener);
        }
        else if (type === "cameraChange") {
            this.eventMap.cameraChange.push(listener);
        }
        else {
            this.eventMap[type].push(listener);
        }
    }
    removeListener(type, listener) {
        let eventIndex = -1;
        // Not sure why the if statement is needed because typescript should understand the typing used in map but it doesn't
        if (type === "stateChange") {
            eventIndex = this.eventMap.stateChange.indexOf(listener);
        }
        else if (type === "cameraChange") {
            eventIndex = this.eventMap.cameraChange.indexOf(listener);
        }
        else {
            eventIndex = this.eventMap[type].indexOf(listener);
        }
        if (eventIndex !== -1) {
            this.eventMap[type].splice(eventIndex, 1);
        }
        else {
            console.warn("Listener to remove can't be found", {
                type,
                listener,
                eventMap: this.eventMap,
            });
        }
    }
    setProduct(product) {
        this.product = product;
    }
    triggerStateChangeEvent(e) {
        this.eventMap.stateChange.forEach((eventHandler) => eventHandler(e));
    }
    triggerCameraChangeEvent(e) {
        this.eventMap.cameraChange.forEach((eventHandler) => eventHandler(e));
    }
    dispose() {
        this.canvas.removeEventListener("mousedown", this.onMouseDown, false);
        this.canvas.removeEventListener("mouseup", this.onMouseUp, false);
        this.canvas.removeEventListener("mousemove", this.onMouseMove, false);
        this.lastIntersection = undefined;
        this.mouseDownPosition = undefined;
    }
    getIntersection(event) {
        const cursorPosition = normalizePosition(event.clientX, event.clientY, this.canvas);
        const intersections = TraceIntersection(cursorPosition, this.camera.threeJsCamera, this.scene);
        if (this.product && intersections) {
            const surfaceMapping = this.product.getSurfaceMapping();
            for (const intersection of intersections) {
                if (surfaceMapping[intersection.object.uuid]) {
                    return {
                        surface: surfaceMapping[intersection.object.uuid],
                        intersection,
                    };
                }
            }
        }
        return undefined;
    }
    // Triggers any event attached to the type
    triggerEvents(type, surface, intersection) {
        const event = {
            type,
            preview: this.preview,
            surfaceId: intersection.object.uuid,
            surface,
            position: this.getLocation(surface, intersection),
            srcElement: this.canvas,
        };
        this.eventMap[type].forEach((listener) => listener(event));
    }
    // eslint-disable-next-line class-methods-use-this
    getLocation(surface, intersection) {
        // Default local coordinates
        const local = { x: 0, y: 0 };
        // Find local coordinates if their UV coordinates are available
        if (surface && intersection.uv) {
            const uvSource = {
                u: intersection.uv.x,
                v: intersection.uv.y,
            };
            const { x, y } = uvToDocumentXY(uvSource, surface);
            local.x = x;
            local.y = y;
        }
        return {
            world: {
                x: intersection.point.x,
                y: intersection.point.y,
                z: intersection.point.z,
            },
            surface: local,
        };
    }
}
//# sourceMappingURL=eventManager.js.map